import React, { useEffect, useMemo, useState } from "react";
import NavbarTP from "./NavbarUtility";

const API_URL = "https://dashboard.technoparts.it/ws/rischio_clienti.php";

const GOLD = "#ebb51b";
const GRAY = "#9a9ca1";

const moneyColumns = new Set([
  "Fido Concesso",
  "Fido Disponibile",
  "Fido Creditsafe",
  "Assicurazione SACE",
  "Esposizione",
  "Esposizione non coperta",
  "Esposizione non coperta da SACE",
  "da incassare",
  "Scaduti",
  "A Scadere",
  "Deposito",
  "Acconti",
  "S.B.F.",
  "S.B.F. Da Presentare",
  "Proforme",
  "Back Order",
  "Liste Aperte",
  "Liste Aperte ODP",
  "Bolle 12M",
  "Fatturato 12M",
  "Fatturato ANNO",
  "Deposito Richiesto (BO)",
  "Pagamento Richiesto (BO + Ft )",
  "Pagamenti Proforme",
  "Dare",
  "Avere",
  "Saldo",
  "Importo Distinta Presentata",
]);

const dateColumns = new Set([
  "Data",
  "Data Scadenza",
  "Data Documento",
]);

const baseColumns = [
  "Codice Conto",
  "ID Cliente",
  "Ragione Sociale",
  "TC",
  "Agente",
  "Operatore",
  "Pagamento",
  "Perc Ordine",
  "Pratica Legale",
  "Canale",
  "Fido Concesso",
  "Fido Disponibile",
  "Fido Creditsafe",
  "Assicurazione SACE",
  "Esposizione",
  "Esposizione non coperta",
  "Esposizione non coperta da SACE",
  "da incassare",
  "Scaduti",
  "A Scadere",
  "Deposito",
  "Acconti",
  "S.B.F.",
  "S.B.F. Da Presentare",
];

const extraColumns = [
  "Proforme",
  "Back Order",
  "Liste Aperte",
  "Liste Aperte ODP",
  "Bolle 12M",
  "Fatturato 12M",
  "Fatturato ANNO",
  "Deposito Richiesto (BO)",
  "Pagamento Richiesto (BO + Ft )",
  "Pagamenti Proforme",
];

const scadenzeColumns = [
  "ID Cliente",
  "Ragione Sociale",
  "Descrizione",
  "Note",
  "ID Scadenza",
  "Data Scadenza",
  "Tipo Pagamento",
  "Esito Pagamento",
  "Data",
  "Dare",
  "Avere",
  "Saldo",
  "Num Documento",
  "Data Documento",
  "Tipo Doc",
  "Num Doc",
  "Tipo Fatt",
  "Num Fatt",
  "Pagamento Doc",
  "ID Distinta Presentata",
  "Importo Distinta Presentata",
  "Scadenze in Distinta Presentata",
];

const plusMonthsIso = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

function RischioClientiIcon({ size = 34, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <circle cx="24" cy="20" r="9" fill={GOLD} />
      <path
        d="M8 49C8 38.5 14.8 32 24 32C30.1 32 35.1 34.9 37.8 39.5C33.9 43.3 31.5 48.6 31.5 54H13C10.2 54 8 51.8 8 49Z"
        fill={GOLD}
      />
      <path
        d="M43 29L56 33.4V43.2C56 52 50.6 58.2 43 61C35.4 58.2 30 52 30 43.2V33.4L43 29Z"
        fill={GRAY}
      />
      <rect x="40.8" y="36" width="4.4" height="11" rx="2.2" fill={GOLD} />
      <circle cx="43" cy="52" r="2.3" fill={GOLD} />
    </svg>
  );
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(String(value).replace(",", "."));
  if (!Number.isFinite(n)) return value;

  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDate(value) {
  if (!value) return "";

  const raw = String(value).trim();
  const parsed = new Date(raw.replace(" ", "T"));

  if (Number.isNaN(parsed.getTime())) return raw;

  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

function cellValue(column, value) {
  if (moneyColumns.has(column)) return formatMoney(value);
  if (dateColumns.has(column)) return formatDate(value);
  return value ?? "";
}

function sumColumn(rows, column) {
  return rows.reduce((sum, row) => {
    const n = Number(String(row?.[column] ?? 0).replace(",", "."));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function scadenzaRowClass(row, ggCorrezione) {
  const saldo = Number(String(row?.Saldo ?? 0).replace(",", "."));
  const dataRaw = row?.Data;
  const tipoFatt = String(row?.["Tipo Fatt"] ?? "").trim();

  if (!dataRaw) return saldo === 0 ? "rc-row-paid" : "rc-row-open";

  const data = new Date(String(dataRaw).replace(" ", "T"));
  if (Number.isNaN(data.getTime())) return "";

  const limite = new Date();
  limite.setHours(0, 0, 0, 0);
  limite.setDate(limite.getDate() + Number(ggCorrezione || 0));

  const limite30 = new Date(limite);
  limite30.setDate(limite30.getDate() - 30);

  if (tipoFatt && data < limite30) return "rc-row-danger";
  if (tipoFatt && data < limite) return "rc-row-warning";
  return saldo === 0 ? "rc-row-paid" : "rc-row-open";
}

const styles = `
  :root {
    --rc-gold: #ebb51b;
    --rc-gold-soft: rgba(235, 181, 27, .16);
    --rc-bg: #080a0d;
    --rc-panel: rgba(20, 22, 27, .62);
    --rc-panel-strong: rgba(25, 28, 34, .78);
    --rc-border: rgba(255, 255, 255, .11);
    --rc-border-soft: rgba(255, 255, 255, .07);
    --rc-text: #f5f6f7;
    --rc-muted: #a6a9b0;
  }

  .rc-page {
    min-height: 100vh;
    color: var(--rc-text);
    background:
      radial-gradient(circle at 12% 5%, rgba(235, 181, 27, .12), transparent 28%),
      radial-gradient(circle at 87% 16%, rgba(120, 128, 145, .12), transparent 28%),
      linear-gradient(145deg, #090a0d 0%, #0f1116 48%, #08090c 100%);
    position: relative;
    overflow-x: hidden;
  }

  .rc-page::before,
  .rc-page::after {
    content: "";
    position: fixed;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    filter: blur(95px);
    pointer-events: none;
    z-index: 0;
    opacity: .35;
  }

  .rc-page::before {
    top: 110px;
    left: -210px;
    background: rgba(235, 181, 27, .22);
  }

  .rc-page::after {
    right: -230px;
    bottom: -100px;
    background: rgba(110, 119, 135, .2);
  }

  .rc-content {
    position: relative;
    z-index: 1;
    padding: 22px 22px 36px;
  }

  .rc-hero,
  .rc-glass,
  .rc-kpi {
    position: relative;
    overflow: hidden;
    background:
      linear-gradient(145deg, rgba(255,255,255,.085), rgba(255,255,255,.025)),
      var(--rc-panel);
    border: 1px solid var(--rc-border);
    box-shadow:
      0 18px 55px rgba(0,0,0,.28),
      inset 0 1px 0 rgba(255,255,255,.12);
    backdrop-filter: blur(24px) saturate(155%);
    -webkit-backdrop-filter: blur(24px) saturate(155%);
  }

  .rc-hero::before,
  .rc-glass::before,
  .rc-kpi::before {
    content: "";
    position: absolute;
    top: 0;
    left: 5%;
    right: 5%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,.45),
      transparent
    );
    pointer-events: none;
  }

  .rc-hero {
    border-radius: 26px;
    padding: 18px 20px;
    margin-bottom: 14px;
  }

  .rc-hero-main {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .rc-icon-shell {
    width: 58px;
    height: 58px;
    border-radius: 18px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    background:
      linear-gradient(145deg, rgba(255,255,255,.12), rgba(255,255,255,.025));
    border: 1px solid rgba(255,255,255,.12);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,.14),
      0 10px 24px rgba(0,0,0,.22);
  }

  .rc-title {
    margin: 0;
    font-size: clamp(1.45rem, 2vw, 2rem);
    font-weight: 750;
    letter-spacing: -.035em;
  }

  .rc-subtitle {
    margin: 3px 0 0;
    color: var(--rc-muted);
    font-size: .9rem;
  }

  .rc-glass {
    border-radius: 24px;
  }

  .rc-filter-panel {
    padding: 16px;
    margin-bottom: 14px;
  }

  .rc-form-label {
    display: block;
    margin-bottom: 6px;
    color: #c9ccd2;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .rc-control {
    width: 100%;
    min-height: 39px;
    color: #f4f5f6 !important;
    background:
      linear-gradient(145deg, rgba(255,255,255,.07), rgba(255,255,255,.025)) !important;
    border: 1px solid rgba(255,255,255,.11) !important;
    border-radius: 12px !important;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.05);
    transition:
      border-color .2s ease,
      box-shadow .2s ease,
      background .2s ease;
  }

  .rc-control:hover {
    border-color: rgba(255,255,255,.18) !important;
  }

  .rc-control:focus {
    color: #fff !important;
    background: rgba(255,255,255,.075) !important;
    border-color: rgba(235,181,27,.58) !important;
    box-shadow: 0 0 0 3px rgba(235,181,27,.11) !important;
  }

  .rc-control::placeholder {
    color: #777c85;
  }

  select.rc-control option {
    color: #111;
    background: #f4f4f4;
  }

  input[type="date"].rc-control {
    color-scheme: dark;
  }

  .rc-check {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 39px;
    padding: 0 3px;
    color: #d7d9dd;
    font-size: .83rem;
  }

  .rc-check input {
    width: 17px;
    height: 17px;
    accent-color: var(--rc-gold);
  }

  .rc-btn {
    min-height: 39px;
    border-radius: 12px;
    padding: 0 18px;
    border: 1px solid rgba(255,255,255,.12);
    font-size: .82rem;
    font-weight: 750;
    letter-spacing: .015em;
    transition:
      transform .18s ease,
      box-shadow .18s ease,
      border-color .18s ease,
      background .18s ease;
  }

  .rc-btn:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .rc-btn-primary {
    color: #141414;
    background: linear-gradient(145deg, #f2c544, #ebb51b 58%, #c9960d);
    border-color: rgba(255,220,105,.55);
    box-shadow:
      0 10px 26px rgba(235,181,27,.19),
      inset 0 1px 0 rgba(255,255,255,.38);
  }

  .rc-btn-primary:hover:not(:disabled) {
    color: #111;
    box-shadow:
      0 14px 32px rgba(235,181,27,.25),
      inset 0 1px 0 rgba(255,255,255,.42);
  }

  .rc-btn-ghost {
    color: #e6e7e9;
    background: rgba(255,255,255,.055);
  }

  .rc-btn-ghost:hover:not(:disabled) {
    color: #fff;
    border-color: rgba(255,255,255,.2);
    background: rgba(255,255,255,.09);
  }

  .rc-kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }

  .rc-kpi {
    min-height: 98px;
    border-radius: 20px;
    padding: 14px 15px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition:
      transform .2s ease,
      border-color .2s ease,
      box-shadow .2s ease;
  }

  .rc-kpi:hover {
    transform: translateY(-2px);
    border-color: rgba(235,181,27,.22);
    box-shadow:
      0 22px 55px rgba(0,0,0,.34),
      0 0 0 1px rgba(235,181,27,.04),
      inset 0 1px 0 rgba(255,255,255,.13);
  }

  .rc-kpi-label {
    color: var(--rc-muted);
    font-size: .71rem;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  .rc-kpi-value {
    margin-top: 10px;
    font-size: clamp(1rem, 1.5vw, 1.35rem);
    font-weight: 760;
    letter-spacing: -.025em;
    white-space: nowrap;
  }

  .rc-kpi-value span {
    color: var(--rc-gold);
    font-size: .75em;
    margin-left: 3px;
  }

  .rc-table-panel {
    margin-bottom: 14px;
  }

  .rc-panel-head {
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 15px;
    border-bottom: 1px solid var(--rc-border-soft);
    background: rgba(255,255,255,.018);
  }

  .rc-panel-title-wrap {
    min-width: 0;
  }

  .rc-panel-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: .92rem;
    font-weight: 760;
  }

  .rc-panel-subtitle {
    margin-top: 3px;
    color: var(--rc-muted);
    font-size: .74rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rc-count {
    flex: 0 0 auto;
    min-width: 32px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #17130a;
    background: linear-gradient(145deg, #f3c849, #ebb51b);
    border: 1px solid rgba(255,220,105,.5);
    box-shadow: inset 0 1px 0 rgba(255,255,255,.34);
    font-size: .75rem;
    font-weight: 800;
  }

  .rc-table-wrap {
    overflow: auto;
    scrollbar-width: none;
  }

  .rc-table-wrap::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .rc-table {
    width: max-content;
    min-width: 100%;
    margin: 0;
    color: #e9eaec;
    border-collapse: separate;
    border-spacing: 0;
    font-size: .77rem;
  }

  .rc-table thead th {
    position: sticky;
    top: 0;
    z-index: 5;
    height: 42px;
    padding: 8px 10px;
    color: #c4c7cc;
    background: rgba(19,21,26,.94);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border-bottom: 1px solid rgba(255,255,255,.1);
    border-right: 1px solid rgba(255,255,255,.045);
    font-size: .67rem;
    font-weight: 760;
    letter-spacing: .055em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .rc-table tbody td {
    height: 40px;
    padding: 8px 10px;
    color: #e1e3e6;
    background: rgba(255,255,255,.012);
    border-bottom: 1px solid rgba(255,255,255,.047);
    border-right: 1px solid rgba(255,255,255,.035);
    white-space: nowrap;
    transition:
      background .16s ease,
      color .16s ease;
  }

  .rc-table tbody tr.rc-clickable {
    cursor: pointer;
  }

  .rc-table tbody tr.rc-clickable:hover td {
    background: rgba(255,255,255,.052);
    color: #fff;
  }

  .rc-table tbody tr.rc-selected td {
    background: rgba(235,181,27,.12);
    border-bottom-color: rgba(235,181,27,.16);
  }

  .rc-table tbody tr.rc-selected:hover td {
    background: rgba(235,181,27,.17);
  }

  .rc-money {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .rc-cell-credit td[data-column="Fido Creditsafe"],
  td.rc-credit {
    background: rgba(75, 166, 220, .09) !important;
  }

  td.rc-sace {
    background: rgba(77, 174, 200, .085) !important;
  }

  td.rc-exposure {
    background: rgba(235,181,27,.065) !important;
  }

  .rc-row-danger td {
    background: rgba(215,72,72,.15) !important;
  }

  .rc-row-warning td {
    background: rgba(235,126,146,.12) !important;
  }

  .rc-row-paid td {
    background: rgba(66,173,108,.11) !important;
  }

  .rc-row-open td {
    background: rgba(235,181,27,.055) !important;
  }

  .rc-empty {
    min-height: 150px;
    display: grid;
    place-items: center;
    padding: 28px 16px;
    color: var(--rc-muted);
    text-align: center;
    font-size: .86rem;
  }

  .rc-alert {
    margin-bottom: 14px;
    padding: 11px 13px;
    border-radius: 16px;
    color: #ffd7d7;
    background:
      linear-gradient(145deg, rgba(255,255,255,.045), rgba(255,255,255,.015)),
      rgba(159,48,48,.17);
    border: 1px solid rgba(255,100,100,.22);
    backdrop-filter: blur(18px);
  }

  .rc-loading-line {
    height: 2px;
    overflow: hidden;
    background: rgba(255,255,255,.04);
  }

  .rc-loading-line::after {
    content: "";
    display: block;
    width: 34%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--rc-gold),
      transparent
    );
    animation: rcLoading 1.2s linear infinite;
  }

  @keyframes rcLoading {
    from { transform: translateX(-120%); }
    to { transform: translateX(360%); }
  }

  @media (max-width: 1199px) {
    .rc-kpi-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 767px) {
    .rc-content {
      padding: 14px 10px 28px;
    }

    .rc-hero {
      padding: 14px;
      border-radius: 20px;
    }

    .rc-hero-main {
      align-items: flex-start;
    }

    .rc-icon-shell {
      width: 50px;
      height: 50px;
      border-radius: 15px;
    }

    .rc-kpi-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .rc-kpi {
      min-height: 88px;
      border-radius: 17px;
      padding: 12px;
    }

    .rc-glass {
      border-radius: 19px;
    }

    .rc-filter-panel {
      padding: 13px;
    }

    .rc-panel-head {
      min-height: 54px;
      padding: 10px 12px;
    }

    .rc-table {
      font-size: .73rem;
    }
  }

  @media (max-width: 430px) {
    .rc-kpi-grid {
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .rc-kpi:last-child {
      grid-column: 1 / -1;
    }
  }
`;

export default function RischioClienti() {
  const [rows, setRows] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [tipiCliente, setTipiCliente] = useState([]);

  const [selectedClient, setSelectedClient] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingScadenze, setLoadingScadenze] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    data_da: "2000-01-01",
    data_a: plusMonthsIso(12),
    gg_correzione: 0,
    cliente: "",
    operatore: "",
    tipo_cliente: "",
    vista: "esposizione",
    pratiche_legali: "escludi",
    intercompany: "tutti",
    calcola_fatt: false,
  });

  const riepilogoColumns = useMemo(
    () =>
      filters.calcola_fatt || filters.vista === "fatturato"
        ? [...baseColumns, ...extraColumns]
        : baseColumns,
    [filters.calcola_fatt, filters.vista]
  );

  const totals = useMemo(
    () => ({
      esposizione: sumColumn(rows, "Esposizione"),
      scaduti: sumColumn(rows, "Scaduti"),
      aScadere: sumColumn(rows, "A Scadere"),
      sbfPresentare: sumColumn(rows, "S.B.F. Da Presentare"),
      sbf: sumColumn(rows, "S.B.F."),
    }),
    [rows]
  );

  async function request(params) {
    const query = new URLSearchParams(params);
    const response = await fetch(`${API_URL}?${query.toString()}`);
    const json = await response.json();

    if (!response.ok || json?.success === false) {
      throw new Error(json?.error || "Errore durante il caricamento.");
    }

    return json;
  }

  async function loadMeta() {
    try {
      const json = await request({ action: "meta" });
      setOperatori(json.operatori || []);
      setTipiCliente(json.tipi_cliente || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadRiepilogo() {
    setLoading(true);
    setError("");
    setSelectedClient(null);
    setScadenze([]);

    try {
      const json = await request({
        action: "riepilogo",
        data_da: filters.data_da,
        data_a: filters.data_a,
        gg_correzione: filters.gg_correzione,
        cliente: filters.cliente,
        operatore: filters.operatore,
        tipo_cliente: filters.tipo_cliente,
        vista: filters.vista,
        pratiche_legali: filters.pratiche_legali,
        intercompany: filters.intercompany,
        calcola_fatt: filters.calcola_fatt ? 1 : 0,
      });

      setRows(json.data || []);
    } catch (e) {
      setRows([]);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadScadenze(row) {
    const idCliente = row?.["ID Cliente"];
    if (!idCliente) return;

    setSelectedClient(row);
    setLoadingScadenze(true);
    setError("");

    try {
      const json = await request({
        action: "scadenze",
        id_cliente: idCliente,
        data_da: filters.data_da,
        data_a: filters.data_a,
        gg_correzione: filters.gg_correzione,
      });

      setScadenze(json.data || []);
    } catch (e) {
      setScadenze([]);
      setError(e.message);
    } finally {
      setLoadingScadenze(false);
    }
  }

  useEffect(() => {
    loadMeta();
    loadRiepilogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function resetFilters() {
    setFilters({
      data_da: "2000-01-01",
      data_a: plusMonthsIso(12),
      gg_correzione: 0,
      cliente: "",
      operatore: "",
      tipo_cliente: "",
      vista: "esposizione",
      pratiche_legali: "escludi",
      intercompany: "tutti",
      calcola_fatt: false,
    });

    setSelectedClient(null);
    setScadenze([]);
  }

  function columnClass(column) {
    const classes = [];

    if (moneyColumns.has(column)) classes.push("rc-money");
    if (column === "Fido Creditsafe") classes.push("rc-credit");
    if (column === "Assicurazione SACE") classes.push("rc-sace");
    if (column === "Esposizione") classes.push("rc-exposure");

    return classes.join(" ");
  }

  return (
    <div className="rc-page">
      <style>{styles}</style>

      <NavbarTP />

      <main className="rc-content">
        
        <form
          className="rc-glass rc-filter-panel"
          onSubmit={(e) => {
            e.preventDefault();
            loadRiepilogo();
          }}
        >
          <div className="row g-2">
            <div className="col-6 col-md-2">
              <label className="rc-form-label">Dal</label>
              <input
                type="date"
                className="form-control form-control-sm rc-control"
                value={filters.data_da}
                onChange={(e) => updateFilter("data_da", e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Al</label>
              <input
                type="date"
                className="form-control form-control-sm rc-control"
                value={filters.data_a}
                onChange={(e) => updateFilter("data_a", e.target.value)}
              />
            </div>

            <div className="col-5 col-md-1">
              <label className="rc-form-label">GG corr.</label>
              <input
                type="number"
                className="form-control form-control-sm rc-control"
                value={filters.gg_correzione}
                onChange={(e) =>
                  updateFilter("gg_correzione", Number(e.target.value || 0))
                }
              />
            </div>

            <div className="col-7 col-md-3">
              <label className="rc-form-label">Cliente</label>
              <input
                type="text"
                className="form-control form-control-sm rc-control"
                placeholder="Ragione sociale o codice"
                value={filters.cliente}
                onChange={(e) => updateFilter("cliente", e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Operatore</label>
              <select
                className="form-select form-select-sm rc-control"
                value={filters.operatore}
                onChange={(e) => updateFilter("operatore", e.target.value)}
              >
                <option value="">Tutti</option>
                {operatori.map((op, index) => (
                  <option
                    key={`${op.codice ?? "op"}-${index}`}
                    value={op.descrizione ?? ""}
                  >
                    {op.descrizione}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Tipo cliente</label>
              <select
                className="form-select form-select-sm rc-control"
                value={filters.tipo_cliente}
                onChange={(e) => updateFilter("tipo_cliente", e.target.value)}
              >
                <option value="">Tutti</option>
                {tipiCliente.map((tipo, index) => (
                  <option
                    key={`${tipo.tipo_cliente ?? "tc"}-${index}`}
                    value={tipo.tipo_cliente ?? ""}
                  >
                    {tipo.tipo_cliente}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Vista</label>
              <select
                className="form-select form-select-sm rc-control"
                value={filters.vista}
                onChange={(e) => updateFilter("vista", e.target.value)}
              >
                <option value="tutti">Tutti</option>
                <option value="esposizione">Con esposizione</option>
                <option value="scaduti">Scaduti</option>
                <option value="a_scadere">A scadere</option>
                <option value="fatturato">Con fatturato</option>
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Pratiche legali</label>
              <select
                className="form-select form-select-sm rc-control"
                value={filters.pratiche_legali}
                onChange={(e) =>
                  updateFilter("pratiche_legali", e.target.value)
                }
              >
                <option value="tutti">Tutte</option>
                <option value="escludi">Escludi pratiche legali</option>
                <option value="solo">Solo pratiche legali</option>
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="rc-form-label">Intercompany</label>
              <select
                className="form-select form-select-sm rc-control"
                value={filters.intercompany}
                onChange={(e) => updateFilter("intercompany", e.target.value)}
              >
                <option value="tutti">Tutti</option>
                <option value="escludi">Escludi intercompany</option>
                <option value="solo">Solo intercompany</option>
              </select>
            </div>

            <div className="col-12 col-md-3 d-flex align-items-end">
              <label className="rc-check">
                <input
                  type="checkbox"
                  checked={filters.calcola_fatt}
                  onChange={(e) =>
                    updateFilter("calcola_fatt", e.target.checked)
                  }
                />
                <span>Calcola fatturato / BO / proforme</span>
              </label>
            </div>

            <div className="col-12 col-md-3 d-flex align-items-end justify-content-md-end">
              <button
                type="submit"
                className="rc-btn rc-btn-primary w-100"
                disabled={loading}
              >
                {loading ? "Caricamento..." : "Aggiorna"}
              </button>
            </div>
          </div>
        </form>

        {error && <div className="rc-alert">{error}</div>}

        <section className="rc-kpi-grid">
          {[
            ["Esposizione", totals.esposizione],
            ["Scaduti", totals.scaduti],
            ["A scadere", totals.aScadere],
            ["S.B.F. da presentare", totals.sbfPresentare],
            ["S.B.F.", totals.sbf],
          ].map(([label, value]) => (
            <article className="rc-kpi" key={label}>
              <div className="rc-kpi-label">{label}</div>
              <div className="rc-kpi-value">
                {formatMoney(value)}
                <span>€</span>
              </div>
            </article>
          ))}
        </section>

        <section className="rc-glass rc-table-panel">
          <div className="rc-panel-head">
            <div className="rc-panel-title-wrap">
              <h2 className="rc-panel-title">
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: GOLD,
                    boxShadow: "0 0 14px rgba(235,181,27,.55)",
                  }}
                />
                Riepilogo clienti
              </h2>
              <div className="rc-panel-subtitle">
                Clicca una riga per visualizzare il dettaglio delle scadenze.
              </div>
            </div>

            <span className="rc-count">{rows.length}</span>
          </div>

          {loading && <div className="rc-loading-line" />}

          <div
            className="rc-table-wrap"
            style={{ maxHeight: "52vh" }}
          >
            <table className="rc-table">
              <thead>
                <tr>
                  {riepilogoColumns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={riepilogoColumns.length}>
                      <div className="rc-empty">Nessun dato disponibile.</div>
                    </td>
                  </tr>
                )}

                {rows.map((row, index) => {
                  const selected =
                    String(selectedClient?.["ID Cliente"] ?? "") ===
                    String(row?.["ID Cliente"] ?? "");

                  return (
                    <tr
                      key={`${row?.["ID Cliente"] ?? "row"}-${index}`}
                      onClick={() => loadScadenze(row)}
                      className={`rc-clickable ${
                        selected ? "rc-selected" : ""
                      }`}
                    >
                      {riepilogoColumns.map((column) => (
                        <td
                          key={column}
                          className={columnClass(column)}
                          data-column={column}
                        >
                          {cellValue(column, row?.[column])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rc-glass rc-table-panel">
          <div className="rc-panel-head">
            <div className="rc-panel-title-wrap">
              <h2 className="rc-panel-title">
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: selectedClient ? GOLD : GRAY,
                    boxShadow: selectedClient
                      ? "0 0 14px rgba(235,181,27,.5)"
                      : "none",
                  }}
                />
                Scadenze cliente
              </h2>

              <div className="rc-panel-subtitle">
                {selectedClient
                  ? `${selectedClient["ID Cliente"]} — ${
                      selectedClient["Ragione Sociale"] ?? ""
                    }`
                  : "Seleziona un cliente dalla tabella superiore."}
              </div>
            </div>

            <span className="rc-count">{scadenze.length}</span>
          </div>

          {loadingScadenze && <div className="rc-loading-line" />}

          {!selectedClient ? (
            <div className="rc-empty">
              Clicca una riga del riepilogo per vedere le scadenze.
            </div>
          ) : loadingScadenze ? (
            <div className="rc-empty">Caricamento scadenze...</div>
          ) : (
            <div
              className="rc-table-wrap"
              style={{ maxHeight: "45vh" }}
            >
              <table className="rc-table">
                <thead>
                  <tr>
                    {scadenzeColumns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {scadenze.length === 0 && (
                    <tr>
                      <td colSpan={scadenzeColumns.length}>
                        <div className="rc-empty">
                          Nessuna scadenza per il cliente selezionato.
                        </div>
                      </td>
                    </tr>
                  )}

                  {scadenze.map((row, index) => (
                    <tr
                      key={`${row?.["ID Scadenza"] ?? "scad"}-${index}`}
                      className={scadenzaRowClass(
                        row,
                        filters.gg_correzione
                      )}
                    >
                      {scadenzeColumns.map((column) => (
                        <td
                          key={column}
                          className={
                            moneyColumns.has(column) ? "rc-money" : ""
                          }
                        >
                          {cellValue(column, row?.[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}